import * as React from 'react';
import type { Collection, SearchLocation, SearchResult } from '@/scopes/search/search/infrastructure/api/types';
import { css, styled } from 'styled-components';
import type { CollectionLabelType, IconGlyph } from '@sunwebgroup/ui-components';
import {
  BulletList,
  CollectionLabel,
  DiscountLabel,
  Icon,
  Label,
  Popover,
  PopoverRoot,
  PopoverTrigger,
  ScorePill,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';

import { useIntl } from '@/intl';
import { PriceInfo } from '@/scopes/common/components/PriceInfo';
import { type AccommodationService } from '../Result';
import { Card } from '../../../../../../../common/components/Card';
import FavoriteButton from './FavoriteButton';
import { ImageCarousel } from './ImageCarousel';
import { LocationTag } from './LocationTag';
import { OverlayLink } from './OverlayLink';
import { ScoreRatings } from './ScoresRatings';
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/useOptimizely';
import { CampaignUsp } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/CampaignUsp';
import type { RoomsParticipantDistribution } from '@/domain/shared/types';
import { ProductCardImage } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardImage';

export interface ProductCardProps {
  accommodationId: number;
  accommodationServices: AccommodationService[];
  arrivalDate: string;
  campaign?: SearchResult['campaign'];
  departureDate: string;
  departureDateRaw?: string;
  durationRaw?: number;
  href: string;
  id: number;
  imageSources: string[];
  isUnavailable: boolean;
  labelIcon?: IconGlyph;
  yieldCampaign?: string;
  yieldCampaignLogoSource?: string;
  locations: SearchLocation[];
  mealPlan?: string;
  name: string;
  partyComposition?: string;
  price?: number;
  priceDiscounts?: {
    discountPercentage: number;
    originalPrice: number;
  };
  rating: number;
  transportType?: string;
  userRating?: number;
  userRatingText: string;
  usps: string[];
  outboundDepartureAirportName: string;
  collection?: Collection;
  isMarketExclusive: boolean;
  isSustainable: boolean;
  participants?: RoomsParticipantDistribution;
  onClick: () => void;
}

export function ProductCard({
  accommodationId,
  accommodationServices,
  arrivalDate,
  campaign,
  departureDate,
  departureDateRaw,
  durationRaw,
  href,
  id,
  imageSources,
  isUnavailable,
  labelIcon,
  yieldCampaign,
  yieldCampaignLogoSource,
  locations,
  mealPlan,
  name,
  partyComposition,
  price,
  priceDiscounts,
  rating,
  transportType,
  userRating,
  userRatingText,
  usps,
  outboundDepartureAirportName,
  collection,
  isMarketExclusive,
  isSustainable,
  participants,
  onClick,
}: ProductCardProps): JSX.Element {
  const intl = useIntl();
  const { isDesktopSm } = useBreakpoints();
  const collectionType: CollectionLabelType = (collection?.id.toLowerCase() || 'default') as CollectionLabelType;
  const { yieldCampaignImageFlag, hideUspMobile, hideCarousel } = useOptimizelyFlagContext();
  return (
    <StyledProductSearchResultCard qa-product-card="true" data-qa="product-card" onClick={onClick}>
      {/* OverlayLink is intentionally empty, because the whole card should be clickable, but also the image carousel should be scrollable & buttons clickable */}
      <OverlayLink href={href} aria-label={name} target={isDesktopSm ? '_blank' : '_self'} />
      <ImageWrapper>
        {hideCarousel ? (
          <ProductCardImage href={href} src={imageSources[0]} />
        ) : (
          <ImageCarousel
            imageIndex={id}
            images={imageSources.map((imageSrc) => ({
              src: imageSrc,
              alt: name,
            }))}
            href={href}
          />
        )}
        {yieldCampaign ? (
          <Label $state="brand" $variant="corner">
            {labelIcon ? <Icon glyph={labelIcon} size="sm" /> : null}
            {yieldCampaign}
          </Label>
        ) : (
          collection && (
            <StyledCollectionLabel $type={collectionType} $variant="corner">
              {collection.name}
            </StyledCollectionLabel>
          )
        )}
        <FavoriteButton productId={accommodationId} />
      </ImageWrapper>
      <TextsWrapper>
        <Typography $variant="heading-md" as="span">
          {name}
        </Typography>
        <ScoreRatings rating={rating} userRating={userRating} aria-label={userRatingText} />
        <LocationTag locations={locations} />
        <UnorderedList $size="small" $variant="multiline" $hideUspFlag={hideUspMobile}>
          {isMarketExclusive && (
            <Item $icon="IconSparkSunweb" data-qa="sunweb-only-usp">
              <Typography $variant="body-md">{intl.formatMessage({ id: 'product_card_sunweb_exclusive' })}</Typography>
            </Item>
          )}
          {isSustainable && (
            <Item $icon="IconSustainable" data-qa="sustainability-usp">
              <Typography $variant="body-md">{intl.formatMessage({ id: 'product_card_sustainable' })}</Typography>
            </Item>
          )}
          <CampaignUsp campaign={campaign} />
          {usps.map((uspText, index) => (
            <UspItem data-qa="acco-usp" $hideUspFlag={hideUspMobile} key={index} $themed>
              {uspText}
            </UspItem>
          ))}
          {accommodationServices.map((service, index) => (
            <BulletList.Item $icon={service.icon} key={index} $themed>
              {service.text}
            </BulletList.Item>
          ))}
        </UnorderedList>
        {isUnavailable ? (
          <PopoverRoot>
            <PopoverTrigger asChild>
              <UnavailableBanner>
                <SpaceBetween direction="x" alignItems="center" justifyContent="center" spacing={tk('spacing_2xs')}>
                  <Typography $variant="body-sm-bold">
                    {intl.formatMessage({
                      id: 'product_card_unavailable_banner_text',
                    })}
                  </Typography>
                  <Icon glyph="IconInformationCircle" size="sm" />
                </SpaceBetween>
              </UnavailableBanner>
            </PopoverTrigger>
            <Popover
              title={intl.formatMessage({
                id: 'product_card_unavailable_banner_text',
              })}
              content={intl.formatMessage({
                id: 'product_card_unavailable_popover_text',
              })}
              align="center"
            />
          </PopoverRoot>
        ) : (
          <PackageBlockAndPriceBoxWrapper>
            <PackageBlockWrapper>
              {mealPlan && partyComposition && (
                <Typography as="div" $variant="body-sm">
                  {mealPlan}, {partyComposition}
                </Typography>
              )}
              <Typography as="div" $variant="body-sm-bold">
                {departureDate} - {arrivalDate}
              </Typography>
              {outboundDepartureAirportName && (
                <AirportNameContainer>
                  <AirportName>
                    <Icon glyph="IconAirplaneDeparture" size="sm" />
                    <Typography as="div" $variant="body-sm">
                      {outboundDepartureAirportName}
                    </Typography>
                  </AirportName>
                </AirportNameContainer>
              )}
            </PackageBlockWrapper>
            {price && (
              <PriceBoxWrapper $abTestEnabled={yieldCampaignImageFlag}>
                {yieldCampaignImageFlag && yieldCampaignLogoSource && (
                  <YieldCampaignLogo
                    src={yieldCampaignLogoSource}
                    alt="yieldCampaignLogo"
                    data-ab-yield-campaign-logo="true"
                  />
                )}
                <PriceInfo
                  accommodationId={accommodationId}
                  departureDateRaw={departureDateRaw}
                  durationRaw={durationRaw}
                  mealPlan={mealPlan}
                  price={price}
                  priceDiscounts={priceDiscounts}
                  transportType={transportType}
                  participants={participants}
                />
              </PriceBoxWrapper>
            )}
          </PackageBlockAndPriceBoxWrapper>
        )}
      </TextsWrapper>
    </StyledProductSearchResultCard>
  );
}

ProductCard.displayName = 'ProductCard';

const StyledProductSearchResultCard = styled(Card)`
  color: ${tk('color_semantic_text')};
  position: relative;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 312px;

  > ${Label} {
    left: 0;
    position: absolute;
    pointer-events: none;
    top: 0;
    max-width: 75%;

    :last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  ${FavoriteButton} {
    position: absolute;
    top: ${tk('spacing_md')};
    right: ${tk('spacing_md')};
  }

  @media (hover: hover) {
    &:hover {
      button {
        display: inline-flex;
      }
    }
  }
`;

const TextsWrapper = styled.div`
  display: grid;
  flex: 1;
  padding: ${tk('spacing_lg')};
`;

const UnorderedList = styled(BulletList.Root)<{ $hideUspFlag: boolean }>`
  margin-bottom: ${tk('spacing_lg')};
  margin-top: ${tk('spacing_lg')};

  // Code for AB test: ID_298
  @media (max-width: ${tk('breakpoint_tablet')}) {
    ${({ $hideUspFlag }) =>
      $hideUspFlag &&
      css`
        margin: 0;

        ${BulletList.Item}:first-child {
          margin-top: ${tk('spacing_lg')};
        }

        ${BulletList.Item}:last-child {
          margin-bottom: ${tk('spacing_lg')};
        }
      `}
  }
`;

const PackageBlockWrapper = styled.div``;

const PackageBlockAndPriceBoxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const UnavailableBanner = styled.button`
  width: 100%;
  background-color: ${tk('color_semantic_bg_subdued')};
  color: ${tk('color_semantic_text_strong')};
  padding: ${tk('spacing_md')} ${tk('spacing_xs')};
  border-radius: ${tk('radius_medium')};
  position: relative; // make it possible to click the button, instead of the link
`;

const PriceBoxWrapper = styled.div<{ $abTestEnabled: boolean }>`
  ${({ $abTestEnabled }) =>
    $abTestEnabled &&
    css`
      ${DiscountLabel} {
        position: relative !important;
        transform: none !important;
      }
    `}
`;

const AirportNameContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;

  bottom: -12px;
  top: 3px;

  font-size: ${tk('font_size_xs')};
  font-weight: ${tk('font_weight_medium')};
  color: ${tk('color_semantic_text_strong')};
  line-height: ${tk('font_line_height_lg')};
`;

const AirportName = styled.div`
  padding-right: ${tk('spacing_sm')};
  display: flex;
  margin-left: ${tk('spacing_xs')};

  span {
    margin-right: ${tk('spacing_xs')};
    margin-left: -${tk('spacing_xs')};
  }
`;

const YieldCampaignLogo = styled.img`
  display: inline;
`;

const StyledCollectionLabel = styled(CollectionLabel)`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;
ProductCard.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`
        ${ImageWrapper} {
          max-width: 255px;
          height: auto;
        }

        ${StyledProductSearchResultCard} {
          display: flex;

          ${ScorePill} {
            ${ScorePill.modSize('sm')};
          }
        }

        ${PriceBoxWrapper} {
          ${DiscountLabel} {
            position: absolute;
            transform: translateY(-100%);
            pointer-events: none; // make it possible to click on the link
          }
        }

        ${UnorderedList} {
          max-width: 75%;
        }
      `;
    case 'desktop':
      return css`
        ${ImageWrapper} {
          flex-shrink: 0;
          max-width: 375px;
          height: auto;
        }
      `;
  }
};

// Code for AB test: ID_298
const UspItem = styled(BulletList.Item)<{ $hideUspFlag: boolean }>`
  @media (max-width: ${tk('breakpoint_tablet')}) {
    display: ${({ $hideUspFlag }) => ($hideUspFlag ? 'none' : 'block')} !important;
  }
`;

const Item = styled(BulletList.Item)`
  align-items: center;
`;
